import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { forkJoin, map, Observable, switchMap, take, of, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { selectAuth, selectStore } from '@store/selectors';
import { OrderType, Store as StoreModel, Authentication, OrderForm, OrderResponse, PaymentStatus } from '@core/models';
import { IResponse, IResponseObjectSuccess, IResponseSuccess } from '@core/interfaces';
import { CheckoutOrderProcessor } from '@features/checkout/domain/processor';
import { OrderPaymentStatusNameEnum, OrderTypeCodeEnum } from '@core/enums';
import { CheckoutPaymentStatusFilter, CheckoutPaymentWayFilter } from '@features/checkout/domain/enum/checkout-order-creation.enum';

@Injectable({
	providedIn: 'root',
})
export class CheckoutService {
	store$: Observable<StoreModel | null>;
	auth$: Observable<Authentication | null>;
	private readonly defaultLimit = 10;
	private readonly defaultOffset = 0;

	constructor(
		private http: HttpClient,
		private store: Store<AppState>,
		private checkoutOrderProcessor: CheckoutOrderProcessor
	) {
		this.store$ = this.store.select(selectStore);
		this.auth$ = this.store.select(selectAuth);
	}

	create(ordenForm: OrderForm): Observable<IResponseObjectSuccess<OrderResponse>> {
		return this.store$.pipe(
			withLatestFrom(this.auth$),
			take(1),
			switchMap(([storeData, auth]) => {
				const clientId = auth?.id;

				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const configuration = this.checkoutOrderProcessor.createOrderCreationConfiguration(
					ordenForm.paymentMethod,
					ordenForm.typeOrder?.nombre
				);

				return forkJoin({
					orderStatus: this.getOrderStatus(configuration.orderStatusFilter),
					payWey: this.getPayWey(configuration.paymentWayFilter),
					payStatus: configuration.paymentStatusFilter
						? this.getPayStatus(configuration.paymentStatusFilter)
						: of({ data: [null] as (PaymentStatus | null)[] }),
					orderType: this.getOrderType(configuration.orderTypeCode, ordenForm.localChannelId),
				}).pipe(
					switchMap(({ orderStatus, payWey, payStatus, orderType }) => {
						const payload = this.checkoutOrderProcessor.createOrderPayload({
							orderForm: ordenForm,
							clientId,
							orderStatus: orderStatus.data,
							paymentWay: payWey.data,
							paymentStatus: payStatus.data,
							orderType: orderType.data,
						});

						const headers = new HttpHeaders({
							'Content-Type': 'application/json',
							authorization: storeData.apiKey,
						});

						return this.http.post<IResponseObjectSuccess<OrderResponse>>(`${environment.microDeliverect}/order`, payload, {
							headers,
						});
					})
				);
			})
		);
	}

	sendEmail(idCuenta: string, correo: string): Observable<IResponse> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const headers = new HttpHeaders({
					'Content-Type': 'application/json',
					authorization: storeData.apiKey,
				});

				return this.http.post<IResponse>(
					`${environment.microCuentas}/pos/cuenta/sendEmail`,
					{ idCuenta, correo },
					{ headers }
				);
			})
		);
	}

	getOrderStatus(filterName: OrderPaymentStatusNameEnum): Observable<IResponseSuccess<PaymentStatus>> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const headers = new HttpHeaders({
					accept: 'application/json',
					Authorization: storeData.apiKey,
				});

				const params = {
					limit: this.defaultLimit.toString(),
					offset: this.defaultOffset.toString(),
				};

				return this.http
					.get<IResponseSuccess<PaymentStatus>>(`${environment.microOrdenes}/pos/estado`, { headers, params })
					.pipe(
						map(response => ({
							...response,
							data: this.checkoutOrderProcessor.filterOrderCatalogResponse(response.data, filterName),
						}))
					);
			})
		);
	}

	getPayStatus(filterName: CheckoutPaymentStatusFilter): Observable<IResponseSuccess<PaymentStatus>> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const headers = new HttpHeaders({
					accept: 'application/json',
					Authorization: storeData.apiKey,
				});

				return this.http
					.get<IResponseSuccess<PaymentStatus>>(`${environment.microPagos}/pos/estado`, { headers })
					.pipe(
						map(response => ({
							...response,
							data: this.checkoutOrderProcessor.filterOrderCatalogResponse(response.data, filterName),
						}))
					);
			})
		);
	}

	getPayWey(filterName: CheckoutPaymentWayFilter): Observable<IResponseSuccess<PaymentStatus>> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const params: Record<string, string> = {
					limit: this.defaultLimit.toString(),
					offset: this.defaultOffset.toString(),
				};

				if (storeData?.idLocal) {
					params['localId'] = storeData?.idLocal;
				}

				const headers = new HttpHeaders({
					accept: 'application/json',
					Authorization: storeData.apiKey,
				});

				return this.http
					.get<IResponseSuccess<PaymentStatus>>(`${environment.microConfiguraciones}/formapago`, { headers, params })
					.pipe(
						map(response => ({
							...response,
							data: this.checkoutOrderProcessor.filterOrderCatalogResponse(response.data, filterName),
						}))
					);
			})
		);
	}

	getOrderType(
		orderTypeCode: OrderTypeCodeEnum,
		localChannelId?: string
	): Observable<IResponseSuccess<OrderType>> {
		return this.store$.pipe(
			take(1),
			switchMap(storeData => {
				if (!storeData?.apiKey) {
					throw new Error('API Key no disponible');
				}

				const params: Record<string, string> = {
					limit: this.defaultLimit.toString(),
					offset: this.defaultOffset.toString(),
				};

				if (storeData?.idLocal) {
					params['localId'] = storeData?.idLocal;
				}

				if (localChannelId) {
					params['localCanalId'] = localChannelId;
				}

				const headers = new HttpHeaders({
					accept: 'application/json',
					Authorization: storeData.apiKey,
				});

				return this.http
					.get<IResponseSuccess<OrderType>>(`${environment.microConfiguraciones}/tiposorden`, { headers, params })
					.pipe(
						map(response => {
							return {
								...response,
								data: this.checkoutOrderProcessor.filterOrderTypes(response.data, orderTypeCode),
							};
						})
					);
			})
		);
	}
}
